import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Alert, Spinner, Row, Col, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserPlus, 
  faUserMinus, 
  faEnvelope, 
  faBuilding, 
  faPhone, 
  faBrain,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faRefresh,
  faBug
} from '@fortawesome/free-solid-svg-icons';
import { type RelatedPartyDiscovery as RelatedPartyDiscoveryType, RelatedPartyRole, EmailScanResult } from '../types/dealIntelligence';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface RelatedPartyDiscoveryProps {
  dealId: string;
  propertyAddress: string;
  onPartyAdded: (party: RelatedPartyDiscoveryType) => void;
  onPartyDismissed: (partyId: string) => void;
}

const RelatedPartyDiscovery: React.FC<RelatedPartyDiscoveryProps> = ({
  dealId,
  propertyAddress,
  onPartyAdded,
  onPartyDismissed
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(true);
  const [discoveredParties, setDiscoveredParties] = useState<RelatedPartyDiscoveryType[]>([]);
  const [scanResult, setScanResult] = useState<EmailScanResult | null>(null);
  const [lastScan, setLastScan] = useState<Date | null>(null);
  const [hasExistingData, setHasExistingData] = useState(false);
  const debugMode = process.env.NODE_ENV === 'development';
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, RelatedPartyRole>>({});
  const [addingParties, setAddingParties] = useState<Set<string>>(new Set());
  const [autoScanCompleted, setAutoScanCompleted] = useState(false); // Prevent duplicate auto-scans
  const [isRefreshLoad, setIsRefreshLoad] = useState(false); // Track if this is a UI refresh load
  const [isAutoScanPending, setIsAutoScanPending] = useState(false); // Track if auto-scan is about to start

  useEffect(() => {
    // Log component initialization
    console.log('🔍 RelatedPartyDiscovery initialized', {
      dealId,
      propertyAddress,
      debugMode
    });

    // Load any existing discoveries on mount - this will handle auto-scanning logic
    loadExistingDiscoveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, propertyAddress, debugMode]);

  /**
   * Loads existing party discoveries from the database
   */
  const loadExistingDiscoveries = async (): Promise<void> => {
    try {
      setIsLoadingExisting(true);
      console.log('🔍 Loading existing discoveries for deal:', dealId);
      
      // Try multiple possible endpoints
      const endpoints = [
        `/deal-intelligence/${dealId}/discoveries`,
        `/deals/${dealId}/related-parties`,
        `/deals/${dealId}/discovered-parties`,
        `/deals/${dealId}/parties`
      ];

      let response = null;
      let usedEndpoint = '';

      for (const endpoint of endpoints) {
        try {
          response = await axiosInstance.get(endpoint);
          usedEndpoint = endpoint;
          console.log(`✅ Successfully connected to: ${endpoint}`);
          console.log(`🔍 [API RESPONSE] Raw response from ${endpoint}:`, {
            status: response.status,
            data: response.data,
            dataType: typeof response.data,
            dataKeys: Object.keys(response.data || {}),
            firstItem: Array.isArray(response.data) ? response.data[0] : 
                      response.data?.data && Array.isArray(response.data.data) ? response.data.data[0] :
                      response.data?.success && response.data?.data?.discoveries ? response.data.data.discoveries[0] : null
          });
          break;
        } catch (error: any) {
          console.log(`❌ Failed endpoint: ${endpoint} (${error.response?.status})`);
          if (error.response?.data) {
            console.log(`❌ Error response from ${endpoint}:`, error.response.data);
          }
          continue;
        }
      }

      if (!response) {
        const errorMsg = 'All related party endpoints failed';
        setApiErrors(prev => [...prev, errorMsg]);
        console.error('❌ ' + errorMsg);
        return;
      }

      // Handle different response structures and transform data
      let rawParties: any[] = [];
      if (response.data.success && response.data.data?.discoveries) {
        rawParties = response.data.data.discoveries;
      } else if (Array.isArray(response.data)) {
        rawParties = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        rawParties = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        // Handle case where parties are directly in response.data (our updated backend format)
        rawParties = response.data;
        console.log('🔍 [API RESPONSE] Using direct response.data format:', rawParties.length, 'parties');
      }

      // Transform API data to match expected UI format  
      const parties: RelatedPartyDiscoveryType[] = rawParties
        .filter((party: any) => {
          // Only filter out dismissed parties - keep approved/existing to show in "Already Assigned" section
          if (party.status === 'dismissed') {
            console.log(`🗑️ [FILTER] Excluding dismissed party: ${party.name || party.email} (status: ${party.status})`);
            return false;
          }
          
          // Filter out empty/placeholder records
          const hasName = party.name && party.name.trim() !== '';
          const hasEmail = party.email && party.email.trim() !== '';
          const hasLeadName = party.lead?.lead_first_name || party.lead?.lead_last_name;
          const hasLeadEmail = party.lead?.lead_email && party.lead.lead_email.trim() !== '';
          const hasConfidence = (party.role_confidence || party.confidence_score || 0) > 0;
          
          // Keep records that have at least name OR email OR lead info OR confidence
          const isValid = hasName || hasEmail || hasLeadName || hasLeadEmail || hasConfidence;
          
          if (!isValid) {
            console.log('🗑️ [FILTER] Excluding empty record:', party);
          }
          
          return isValid;
        })
        .map((party: any) => {
          // Enhanced logging for individual party transformation
          console.log('🔍 [TRANSFORM] Individual party data:', {
            original: party,
            name_sources: {
              name: party.name,
              email: party.email,
              lead_first_name: party.lead?.lead_first_name,
              lead_last_name: party.lead?.lead_last_name
            },
            confidence_sources: {
              confidence_score: party.confidence_score,
              role_confidence: party.role_confidence
            },
            role_sources: {
              suggested_role: party.suggested_role,
              role: party.role
            }
          });

          // Better name resolution with lead data
          let displayName = party.name;
          if (!displayName && party.lead) {
            const firstName = party.lead.lead_first_name || '';
            const lastName = party.lead.lead_last_name || '';
            displayName = `${firstName} ${lastName}`.trim();
          }
          if (!displayName && party.email) {
            displayName = party.email.split('@')[0]; // Use email username if no name
          }
          if (!displayName) {
            displayName = 'Unknown Contact';
          }

          // Better email resolution
          const email = party.email || party.lead?.lead_email || '';

          // Better confidence score handling
          const confidence = party.role_confidence || party.confidence_score || 0;

          return {
            id: party.id?.toString() || `party-${Date.now()}-${Math.random()}`,
            name: displayName,
            email: email,
            phone: party.phone || '',
            company: party.company || '',
            suggested_role: party.role || party.suggested_role || 'other',
            confidence_score: confidence,
            discovery_source: party.discovery_source || 'email',
            discovery_metadata: party.discovery_metadata || {
              email_subject: '',
              email_date: party.created_at || party.added_at,
              email_snippet: '',
              detection_keywords: []
            },
            status: (party.status === 'approved' || party.status === 'active' || party.status === 'assigned' || party.status === 'existing') ? 'approved' : 'pending',
            added_at: party.added_at || party.created_at || new Date().toISOString(),
            first_interaction_date: party.first_interaction_date,
            last_interaction_date: party.last_interaction_date
          };
        });

      setDiscoveredParties(parties);
      setHasExistingData(rawParties.length > 0);

      // Debug status distribution
      console.log('🔍 [STATUS DEBUG] Party status distribution:', {
        total: parties.length,
        approved: parties.filter(p => p.status === 'approved').length,
        pending: parties.filter(p => p.status === 'pending').length,
        sample_statuses: parties.slice(0, 5).map(p => ({
          name: p.name,
          original_status: rawParties.find(rp => rp.id === p.id || rp.email === p.email)?.status,
          mapped_status: p.status
        }))
      });
      console.log(`✅ Loaded ${parties.length} existing discoveries from ${usedEndpoint}`);
      
      // Enhanced debugging for data transformation
      if (parties.length > 0) {
        console.log('🔍 [DATA] Sample transformed party data:', {
          raw_sample: rawParties[0],
          transformed_sample: parties[0],
          total_raw: rawParties.length,
          total_transformed: parties.length
        });
      } else if (rawParties.length > 0) {
        console.log('⚠️ [DATA] Raw parties found but none transformed:', {
          raw_count: rawParties.length,
          raw_sample: rawParties[0]
        });
      }

      // Set last scan time if available
      if (response.data.data?.last_scan) {
        setLastScan(new Date(response.data.data.last_scan));
      }

      // 🎯 SMART WORKFLOW: Auto-scan when no existing data found
      console.log(`🎯 [SMART WORKFLOW] Data check results:`, {
        rawParties_count: rawParties.length,
        parties_count: parties.length,
        propertyAddress: !!propertyAddress,
        hasPropertyAddress: propertyAddress && propertyAddress.trim().length > 0
      });

      if (rawParties.length > 0) {
        console.log(`✅ Found ${rawParties.length} existing records in database (${parties.length} displayed after filtering)`);
        setHasExistingData(true);
        // Show success message that data was loaded from database
        if (parties.length > 0) {
          console.log(`📊 Displaying ${parties.length} parties from existing data`);
          toast.success(`📊 Loaded ${parties.length} related parties from database`);
        } else {
          console.log(`ℹ️ Found ${rawParties.length} records but none meet display criteria (already assigned, dismissed, or low confidence)`);
          toast.success(`✅ Found ${rawParties.length} existing records (all already assigned or processed)`);
        }
      } else {
        console.log(`📭 No existing data found in database. Checking if auto-scan should trigger...`);
        setHasExistingData(false);
        
        // AUTO-SCAN LOGIC: Trigger email scan when no existing parties found
        // 🛡️ DUPLICATE PREVENTION: Only auto-scan if not already completed and not a refresh load
        if (propertyAddress && propertyAddress.trim().length > 0 && !autoScanCompleted && !isRefreshLoad) {
          console.log(`🔄 [AUTO-SCAN] No existing discoveries found, initiating auto-scan for property: ${propertyAddress}`);
          setAutoScanCompleted(true); // Mark auto-scan as initiated to prevent duplicates
          setIsAutoScanPending(true); // Show "Auto-scan starting..." state
          // Small delay to allow UI to render the "Ready to scan emails" state briefly
          setTimeout(() => {
            setIsAutoScanPending(false); // Clear pending state before starting actual scan
            handleScanEmails();
          }, 1500); // 1.5-second delay for better UX
        } else if (autoScanCompleted) {
          console.log(`🛡️ [AUTO-SCAN] Auto-scan already completed, skipping duplicate scan`);
        } else if (isRefreshLoad) {
          console.log(`🔄 [AUTO-SCAN] This is a UI refresh load, skipping auto-scan`);
        } else {
          console.log(`⚠️ [AUTO-SCAN] No property address available, cannot auto-scan`);
        }
      }

    } catch (error: any) {
      const errorMsg = `Error loading existing discoveries: ${error.message}`;
      setApiErrors(prev => [...prev, errorMsg]);
      setHasExistingData(false);
      console.error('❌ ' + errorMsg, error);
      
      // AUTO-SCAN FALLBACK: If we can't load existing data, try auto-scan as fallback
      console.log('❌ Failed to load existing data. Attempting auto-scan as fallback...');
      setDiscoveredParties([]); // Ensure clean state
      
      // 🛡️ DUPLICATE PREVENTION: Only auto-scan fallback if not already completed and not a refresh load
      if (propertyAddress && propertyAddress.trim().length > 0 && !autoScanCompleted && !isRefreshLoad) {
        console.log(`🔄 [AUTO-SCAN FALLBACK] Initiating email scan for property: ${propertyAddress}`);
        setAutoScanCompleted(true); // Mark auto-scan as initiated to prevent duplicates
        setIsAutoScanPending(true); // Show "Auto-scan starting..." state
        setTimeout(() => {
          setIsAutoScanPending(false); // Clear pending state before starting actual scan
          handleScanEmails();
        }, 2000); // Slightly longer delay for error recovery
      } else if (autoScanCompleted) {
        console.log(`🛡️ [AUTO-SCAN FALLBACK] Auto-scan already completed, skipping duplicate fallback scan`);
      } else if (isRefreshLoad) {
        console.log(`🔄 [AUTO-SCAN FALLBACK] This is a UI refresh load, skipping fallback auto-scan`);
      } else {
        console.log(`⚠️ [AUTO-SCAN FALLBACK] No property address available, user must manually scan`);
      }
    } finally {
      setIsLoadingExisting(false);
    }
  };

  /**
   * Initiates email scanning for related parties
   */
  const handleScanEmails = async () => {
    // 🛡️ DUPLICATE PREVENTION: Prevent multiple simultaneous scans
    if (isScanning) {
      console.log('🛡️ [SCAN GUARD] Scan already in progress, ignoring duplicate request');
      return;
    }

    console.log('🏠 [DEBUG] Property address check:', {
      propertyAddress: propertyAddress,
      propertyAddressType: typeof propertyAddress,
      propertyAddressLength: propertyAddress?.length,
      propertyAddressTrimmed: propertyAddress?.trim(),
      dealId: dealId
    });

    if (!propertyAddress || !propertyAddress.trim()) {
      const errorMsg = 'Property address is required for email scanning';
      console.error('❌ [DEBUG] No property address available for scanning');
      toast.error(errorMsg);
      setApiErrors(prev => [...prev, errorMsg]);
      return;
    }

    setIsScanning(true);
    setApiErrors([]);

    try {
      console.log('📧 [SCAN] Starting Gmail OAuth scan for property address:', propertyAddress);
      
      // Direct API call to Enhanced Gmail OAuth scan endpoint
      const response = await axiosInstance.post(`/deals/${dealId}/emails/scan-database`, {
        property_address: propertyAddress.trim(),
        lookback_days: 30,
        confidence_threshold: 0.6
      });

      console.log('✅ [ENHANCED SCAN] Enhanced Gmail scan response:', response.data);

      // Handle the enhanced response with auto-assigned parties
      if (response.data?.auto_assigned_parties && response.data.auto_assigned_parties.length > 0) {
        const autoAssignedParties = response.data.auto_assigned_parties;
        const scanSummary = response.data.scan_summary;
        const emailsSaved = response.data.emails_saved_to_database || 0;
        
        console.log(`🎯 [ENHANCED SCAN] Auto-assigned ${autoAssignedParties.length} parties`);
        console.log(`📊 [ENHANCED SCAN] Summary:`, scanSummary);
        console.log(`💾 [ENHANCED SCAN] Emails saved: ${emailsSaved}`);
        
        // Transform to our expected format with enhanced data
        const transformedParties = autoAssignedParties.map((party: any) => ({
          id: party.id || `party-${Date.now()}-${Math.random()}`,
          name: party.name || party.email,
          email: party.email,
          phone: party.phone || '',
          role: party.role || 'unknown',
          suggested_role: party.role || 'unknown',
          confidence: party.confidence || 0.5,
          confidence_score: party.confidence_score || 0,
          discovery_source: 'gmail_oauth_enhanced',
          discovery_metadata: party.discovery_metadata || {},
          discovered_at: party.discovered_at || new Date().toISOString(),
          status: party.status || 'assigned', // new, existing, or assigned
          lead_id: party.lead_id
        }));

        setDiscoveredParties(transformedParties);
        setLastScan(new Date());
        
        // Enhanced toast message with comprehensive summary
        const newContactsCount = scanSummary?.new_contacts_created || 0;
        const existingContactsCount = scanSummary?.existing_contacts_linked || 0;
        const toastMessage = `🎯 Enhanced scan complete! Auto-assigned ${autoAssignedParties.length} parties, saved ${emailsSaved} emails (${newContactsCount} new contacts, ${existingContactsCount} existing)`;
        toast.success(toastMessage);
        
        // Store scan result for display
        setScanResult({
          total_emails_scanned: response.data.total_emails_scanned || 0,
          matches_found: autoAssignedParties.length,
          parties_discovered: transformedParties,
          scan_timestamp: new Date().toISOString(),
          property_address: response.data.property_address || propertyAddress,
          date_range: response.data.date_range || {
            start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            end: new Date().toISOString()
          }
        });

        // 🔄 REFRESH UI: Reload existing parties to show newly auto-assigned parties in "Already Assigned" section
        console.log('🔄 [UI REFRESH] Reloading existing parties to display auto-assigned parties...');
        // Clear the discovered parties since they've been auto-assigned and will show in "Already Assigned"
        setDiscoveredParties([]);
        setTimeout(() => {
          setIsRefreshLoad(true); // Mark as refresh load to prevent auto-scan
          loadExistingDiscoveries().then(() => {
            setIsRefreshLoad(false); // Reset refresh load flag
          });
        }, 1000); // Small delay to ensure database writes are complete
        
      } else {
        console.log('ℹ️ [SCAN] No parties found in enhanced Gmail scan');
        setDiscoveredParties([]);
        setLastScan(new Date());
        
        // Set scan result even when no parties found
        setScanResult({
          total_emails_scanned: response.data?.total_emails_scanned || 0,
          matches_found: 0,
          parties_discovered: [],
          scan_timestamp: new Date().toISOString(),
          property_address: propertyAddress,
          date_range: {
            start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            end: new Date().toISOString()
          }
        });
        
        toast.success('Email scan complete. No related parties found in your Gmail emails for this property address.');
        
        // 🔄 REFRESH UI: Still reload existing parties in case there are previously assigned parties to display
        console.log('🔄 [UI REFRESH] Reloading existing parties after scan (no new parties found)...');
        setTimeout(() => {
          setIsRefreshLoad(true); // Mark as refresh load to prevent auto-scan
          loadExistingDiscoveries().then(() => {
            setIsRefreshLoad(false); // Reset refresh load flag
          });
        }, 500); // Shorter delay since no database writes occurred
      }

    } catch (error: any) {
      console.error('❌ [SCAN] Gmail OAuth scan failed:', error);
      
      let errorMessage = 'Failed to scan emails for related parties';
      
      // Handle "scan already in progress" as a soft warning, not an error
      if (error.response?.status === 409 || error.response?.data?.message?.includes('already in progress')) {
        console.log('ℹ️ [SCAN] Scan already in progress - showing gentle notification');
        toast('📧 Email scan is already running. Please wait for it to complete.', {
          duration: 3000,
          style: { background: '#3498db', color: 'white' }
        });
        return; // Don't treat this as an error
      } else if (error.response?.status === 401) {
        errorMessage = 'Gmail OAuth authentication failed. Please reconnect your Gmail account in Settings > Email Settings.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Gmail OAuth connection not found. Please connect your Gmail account in Settings > Email Settings.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
      setApiErrors(prev => [...prev, errorMessage]);
    } finally {
      setIsScanning(false);
      setIsAutoScanPending(false); // Clear pending state in case of error
    }
  };

  /**
   * Handle role selection for a party
   */
  const handleRoleChange = (partyId: string, newRole: RelatedPartyRole) => {
    setSelectedRoles(prev => ({
      ...prev,
      [partyId]: newRole
    }));
  };

  /**
   * Adds a discovered party to the deal with improved workflow
   * No page refresh, uses selected role, updates local state
   */
  const handleAddParty = async (event: React.MouseEvent<HTMLButtonElement>, party: RelatedPartyDiscoveryType) => {
    // Prevent default form submission behavior
    event.preventDefault();
    event.stopPropagation();
    
    const selectedRole = selectedRoles[party.id] || party.suggested_role;
    
    // Prevent multiple clicks
    if (addingParties.has(party.id)) {
      return;
    }

    setAddingParties(prev => new Set(prev).add(party.id));
    
    try {
      console.log('🔍 [ADD PARTY] Adding party:', party.name, 'with role:', selectedRole);
      console.log('🔍 [ADD PARTY] Status change: pending → active (database), pending → approved (frontend)');
      
      // Use the correct updatePartyStatus endpoint for deal_related_parties table
      const response = await axiosInstance.patch(`/deals/${dealId}/intelligence/related-parties/${party.id}`, {
        status: 'active',
        role: selectedRole
      });

      // Update local state immediately - no page refresh
      // Remove the party from discovered parties since it's now added to the deal
      setDiscoveredParties(prev => prev.filter(p => p.id !== party.id));
      
      console.log(`✅ [ADD PARTY] Status updated: ${party.name} moved from "Pending" to "Already Assigned" section`);
      
      // Remove from selectedRoles
      setSelectedRoles(prev => {
        const newRoles = { ...prev };
        delete newRoles[party.id];
        return newRoles;
      });

      // Notify parent component
      const updatedParty = { ...party, suggested_role: selectedRole };
      onPartyAdded(updatedParty);
      
      // Enhanced success message
      const responseData = response.data?.data;
      const roleDisplayName = selectedRole.replace('_', ' ');
      if (responseData?.isNewContact) {
        toast.success(`✅ ${party.name} added as new contact and moved to "Already Assigned" as ${roleDisplayName}`);
      } else {
        toast.success(`🔗 ${party.name} linked to existing contact and moved to "Already Assigned" as ${roleDisplayName}`);
      }
      
      console.log('✅ [ADD PARTY] Successfully moved to "Already Assigned" without page refresh');
      
    } catch (error: any) {
      console.error('❌ [ADD PARTY] Error adding party:', error);
      toast.error(`Failed to add party: ${error.response?.data?.message || error.message}`);
    } finally {
      setAddingParties(prev => {
        const newSet = new Set(prev);
        newSet.delete(party.id);
        return newSet;
      });
    }
  };

  /**
   * Dismisses a discovered party with improved workflow
   */
  const handleDismissParty = async (event: React.MouseEvent<HTMLButtonElement>, party: RelatedPartyDiscoveryType) => {
    // Prevent default form submission behavior
    event.preventDefault();
    event.stopPropagation();
    
    try {
      console.log('🔍 [DISMISS PARTY] Dismissing party:', party.name);
      
      // Mark as dismissed in database using the correct endpoint
      await axiosInstance.patch(`/deals/${dealId}/intelligence/related-parties/${party.id}`, {
        status: 'dismissed'
      });

      // Update local state immediately - no page refresh
      setDiscoveredParties(prev => prev.filter(p => p.id !== party.id));
      
      // Remove from selectedRoles
      setSelectedRoles(prev => {
        const newRoles = { ...prev };
        delete newRoles[party.id];
        return newRoles;
      });

      // Notify parent component
      onPartyDismissed(party.id);
      
      toast.success(`${party.name} dismissed`);
      console.log('✅ [DISMISS PARTY] Successfully dismissed without page refresh');
      
    } catch (error: any) {
      console.error('❌ [DISMISS PARTY] Error dismissing party:', error);
      toast.error(`Failed to dismiss party: ${error.message}`);
    }
  };

  /**
   * Gets role display name
   */
  const getRoleDisplayName = (role: RelatedPartyRole | undefined | null): string => {
    if (!role) return 'Unknown Role';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Gets confidence color for enhanced scoring system
   */
  const getConfidenceColor = (confidence: number | undefined | null): string => {
    if (!confidence) return 'secondary';
    
    // If it's a score out of 200 (new system)
    if (confidence >= 80) return 'success';
    if (confidence >= 50) return 'warning';
    if (confidence >= 30) return 'info';
    
    // If it's a percentage (legacy system)
    if (confidence <= 1) {
      if (confidence >= 0.8) return 'success';
      if (confidence >= 0.6) return 'warning';
      return 'info';
    }
    
    return 'secondary';
  };

  /**
   * Gets role icon based on role type
   */
  const getRoleIcon = (role: string | undefined) => {
    switch (role) {
      case 'buyer_agent':
      case 'listing_agent':
      case 'buyer_broker':
      case 'listing_broker':
        return <FontAwesomeIcon icon={faBuilding} className="text-primary" />;
      case 'title_company':
        return <FontAwesomeIcon icon={faBuilding} className="text-warning" />;
      case 'lender':
      case 'loan_officer':
        return <FontAwesomeIcon icon={faBuilding} className="text-success" />;
      case 'home_inspector':
        return <FontAwesomeIcon icon={faBuilding} className="text-info" />;
      case 'attorney':
        return <FontAwesomeIcon icon={faBuilding} className="text-dark" />;
      case 'insurance_agent':
        return <FontAwesomeIcon icon={faBuilding} className="text-info" />;
      case 'escrow_officer':
        return <FontAwesomeIcon icon={faBuilding} className="text-warning" />;
      case 'appraiser':
        return <FontAwesomeIcon icon={faBuilding} className="text-success" />;
      case 'contractor':
        return <FontAwesomeIcon icon={faBuilding} className="text-warning" />;
      case 'photographer':
      case 'stager':
      case 'moving_company':
      case 'home_warranty':
      case 'marketing':
        return <FontAwesomeIcon icon={faBuilding} className="text-secondary" />;
      case 'office_manager':
        return <FontAwesomeIcon icon={faBuilding} className="text-info" />;
      case 'buyer':
      case 'seller':
        return <FontAwesomeIcon icon={faBuilding} className="text-primary" />;
      default:
        return <FontAwesomeIcon icon={faBuilding} className="text-secondary" />;
    }
  };

  /**
   * Formats date for display
   */
  const formatScanDate = (date: Date | null): string => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleDateString();
  };

  /**
   * Get available roles for dropdown
   */
  const getAvailableRoles = (): { value: RelatedPartyRole; label: string }[] => {
    return [
      { value: 'buyer', label: 'Buyer' },
      { value: 'seller', label: 'Seller' },
      { value: 'buyer_agent', label: 'Buyer Agent' },
      { value: 'listing_agent', label: 'Listing Agent' },
      { value: 'buyer_broker', label: 'Buyer Broker' },
      { value: 'listing_broker', label: 'Listing Broker' },
      { value: 'lender', label: 'Lender' },
      { value: 'loan_officer', label: 'Loan Officer' },
      { value: 'title_company', label: 'Title Company' },
      { value: 'escrow_officer', label: 'Escrow Officer' },
      { value: 'home_inspector', label: 'Home Inspector' },
      { value: 'appraiser', label: 'Appraiser' },
      { value: 'attorney', label: 'Attorney' },
      { value: 'insurance_agent', label: 'Insurance Agent' },
      { value: 'contractor', label: 'Contractor' },
      { value: 'home_warranty', label: 'Home Warranty' },
      { value: 'moving_company', label: 'Moving Company' },
      { value: 'photographer', label: 'Photographer' },
      { value: 'stager', label: 'Stager' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'office_manager', label: 'Office Manager' },
      { value: 'other', label: 'Other' }
    ];
  };

  return (
    <Card className="mb-4" style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(0, 212, 255, 0.2)',
      borderRadius: '16px'
    }}>
      <Card.Header className="d-flex justify-content-between align-items-center" style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '16px 16px 0 0'
      }}>
        <div>
          <h5 className="mb-1">
            <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#667eea' }} />
            AI Party Discovery
          </h5>
          <small className="text-muted">
            {isLoadingExisting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Checking for existing data...
              </>
            ) : isAutoScanPending ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Auto-scan starting...
              </>
            ) : isScanning ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Scanning Gmail emails...
              </>
            ) : hasExistingData ? (
              <>
                ✅ Data loaded from database
                {scanResult && (
                  <span className="ms-2">
                    • {scanResult.total_emails_scanned} emails scanned 
                    • {scanResult.matches_found} matches found
                  </span>
                )}
              </>
            ) : (
              <>
                📧 Ready to scan emails
                {lastScan && (
                  <span className="ms-2">• Last scan: {formatScanDate(lastScan)}</span>
                )}
              </>
            )}
          </small>
        </div>
        <div className="d-flex gap-2">
          {debugMode && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={async () => {
                try {
                  toast.success('🔍 Running diagnostic...');
                  const { debugRelatedParties } = await import('utils/debugRelatedParties');
                  const result = await debugRelatedParties(dealId);
                  console.log('🔍 Debug Result:', result);
                  toast.success('🔍 Diagnostic complete - check console');
                } catch (error) {
                  console.error('🔍 Debug Error:', error);
                  toast.error('🔍 Diagnostic failed');
                }
              }}
              title="Run diagnostic tests"
            >
              <FontAwesomeIcon icon={faBug} />
            </Button>
          )}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {hasExistingData 
                  ? "Scan emails again to find new parties (existing data shown below)"
                  : "Scan Gmail for related parties using the property address"
                }
              </Tooltip>
            }
          >
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={handleScanEmails}
              disabled={isScanning || isLoadingExisting || isAutoScanPending}
              style={{
                borderColor: '#667eea',
                color: '#667eea'
              }}
            >
            {isLoadingExisting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Loading...
              </>
            ) : isAutoScanPending ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Starting...
              </>
            ) : isScanning ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Scanning...
              </>
            ) : hasExistingData ? (
              <>
                <FontAwesomeIcon icon={faRefresh} className="me-2" />
                Rescan Emails
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRefresh} className="me-2" />
                Scan Emails
              </>
            )}
          </Button>
          </OverlayTrigger>
        </div>
      </Card.Header>

      <Card.Body>
        {/* Show error information only for genuine errors (not scanning states) */}
        {apiErrors.length > 0 && debugMode && !isScanning && !isLoadingExisting && !isAutoScanPending && (
          <Alert variant="warning" className="mb-3">
            <h6>🔧 Debug Information:</h6>
            <ul className="mb-0">
              {apiErrors.filter(error => 
                !error.includes('already in progress') && 
                !error.includes('wait for completion')
              ).map((error, index) => (
                <li key={index}><small>{error}</small></li>
              ))}
            </ul>
            <small className="d-block mt-2">
              <strong>Property Address:</strong> "{propertyAddress}"<br/>
              <strong>Deal ID:</strong> {dealId}
            </small>
          </Alert>
        )}

        {/* Show already assigned parties */}
        {discoveredParties.filter(p => ['approved', 'active', 'assigned', 'existing'].includes(p.status)).length > 0 && (
          <div className="mb-4">
            <h6 className="text-success mb-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              {discoveredParties.filter(p => ['approved', 'active', 'assigned', 'existing'].includes(p.status)).length} Already Assigned:
            </h6>
            <div className="row">
              {discoveredParties.filter(p => ['approved', 'active', 'assigned', 'existing'].includes(p.status)).map((party) => (
                <div key={party.id} className="col-md-6 mb-2">
                  <div className="small p-2 bg-light rounded">
                    <strong>{party.name}</strong> ({party.suggested_role})
                    {party.email && <div className="text-muted">{party.email}</div>}
                    <Badge bg="success" className="mt-1">{party.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {discoveredParties.filter(p => p.status === 'pending').length === 0 && !isScanning && (
          <Alert variant="info" className="mb-0">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>No meaningful parties found.</strong>
            <div className="mt-2 small">
              {lastScan ? (
                <>Last scan: {formatScanDate(lastScan)}. Try scanning again if new emails have arrived.</>
              ) : (
                <>No previous scan detected. Click "Scan Emails" to discover parties from your Gmail.</>
              )}
            </div>
          </Alert>
        )}

        {discoveredParties.filter(p => p.status === 'pending').length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0">
                Found {discoveredParties.filter(p => p.status === 'pending').length} potential partie{discoveredParties.filter(p => p.status === 'pending').length !== 1 ? 's' : ''}:
              </h6>
              <div className="d-flex gap-2">
                {discoveredParties.filter(p => p.status === 'pending' && p.confidence_score >= 80).length > 0 && (
                  <Badge bg="success" className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    {discoveredParties.filter(p => p.status === 'pending' && p.confidence_score >= 80).length} High Confidence
                  </Badge>
                )}
                {discoveredParties.filter(p => p.status === 'pending' && p.confidence_score >= 50 && p.confidence_score < 80).length > 0 && (
                  <Badge bg="warning" className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {discoveredParties.filter(p => p.status === 'pending' && p.confidence_score >= 50 && p.confidence_score < 80).length} Medium Confidence
                  </Badge>
                )}
              </div>
            </div>

            {discoveredParties.filter(p => p.status === 'pending').map((party, index) => (
              <Card key={party.id} className="mb-3" style={{
                border: '1px solid rgba(0, 212, 255, 0.1)',
                borderRadius: '12px'
              }}>
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md={5}>
                      <div className="d-flex align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          {getRoleIcon(party.suggested_role)}
                          <strong className="text-primary ms-2">{party.name || 'Unknown Name'}</strong>
                        </div>
                        <div className="ms-auto d-flex gap-1">
                          <Badge 
                            bg={getConfidenceColor(party.confidence_score)} 
                            className="d-flex align-items-center"
                          >
                            {party.confidence_score > 1 ? `${Math.round(party.confidence_score)}%` : `${Math.round((party.confidence_score || 0) * 100)}%`} confidence
                          </Badge>
                          {party.confidence_score >= 80 && (
                            <Badge bg="success" className="d-flex align-items-center">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                              High
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="small text-muted mb-2">
                        <div>
                          <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                          {party.email || 'No email'}
                        </div>
                        {party.phone && (
                          <div>
                            <FontAwesomeIcon icon={faPhone} className="me-1" />
                            {party.phone}
                          </div>
                        )}
                        {party.company && (
                          <div>
                            <FontAwesomeIcon icon={faBuilding} className="me-1" />
                            {party.company}
                          </div>
                        )}
                      </div>

                      {party.discovery_metadata?.email_subject && (
                        <div className="small text-muted mt-1">
                          <strong>From email:</strong> "{party.discovery_metadata.email_subject}"
                        </div>
                      )}
                    </Col>

                    <Col md={4}>
                      <div className="mb-2">
                        <Form.Label className="small fw-bold mb-1">Role Assignment:</Form.Label>
                        <Form.Select 
                          size="sm"
                          value={selectedRoles[party.id] || party.suggested_role}
                          onChange={(e) => handleRoleChange(party.id, e.target.value as RelatedPartyRole)}
                          disabled={addingParties.has(party.id)}
                        >
                          {getAvailableRoles().map(role => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                              {role.value === party.suggested_role && ' (Suggested)'}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      
                      <div className="small text-muted">
                        <strong>AI Suggested:</strong> {getRoleDisplayName(party.suggested_role)}
                      </div>
                    </Col>

                    <Col md={3} className="text-end">
                      <div className="d-flex flex-column gap-2">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Add {party.name} to this deal as {getRoleDisplayName(selectedRoles[party.id] || party.suggested_role)}</Tooltip>}
                        >
                          <Button
                            variant="success"
                            size="sm"
                            type="button"
                            onClick={(e) => handleAddParty(e, party)}
                            disabled={addingParties.has(party.id)}
                            className="d-flex align-items-center justify-content-center"
                          >
                            {addingParties.has(party.id) ? (
                              <>
                                <Spinner size="sm" animation="border" className="me-1" />
                                Adding...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                                Add Party
                              </>
                            )}
                          </Button>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Dismiss this suggestion</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            type="button"
                            onClick={(e) => handleDismissParty(e, party)}
                            disabled={addingParties.has(party.id)}
                            className="d-flex align-items-center justify-content-center"
                          >
                            <FontAwesomeIcon icon={faUserMinus} className="me-1" />
                            Dismiss
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </Col>
                  </Row>

                  {party.discovery_metadata?.email_snippet && (
                    <Row className="mt-2">
                      <Col>
                        <div className="small text-muted p-2 bg-light rounded">
                          <strong>Email excerpt:</strong> {party.discovery_metadata.email_snippet}
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        {scanResult && (
          <div className="mt-3 p-3 bg-light rounded">
            <small className="text-muted">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
              <strong>Scan Summary:</strong> Searched {scanResult.total_emails_scanned} emails 
              from {new Date(scanResult.date_range.start).toLocaleDateString()} 
              to {new Date(scanResult.date_range.end).toLocaleDateString()}.
              Found {scanResult.matches_found} emails mentioning "{scanResult.property_address}".
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default RelatedPartyDiscovery;